/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './SuspendPage';

const SuspendPageManager: ComponentManagerFn<'SuspendPage'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SuspendPage'>(platform, 'SuspendPage'),
    type: 'SuspendPage',
    name: 'Suspend Page',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'DataStoreReference',
          'FlexPanel',
          'HorizontalSplitPanel',
          'Paper',
          'TabPanel',
          'VerticalSplitPanel',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      suspendCondition: {
        type: 'expressionOnly',
        returnType: () => 'boolean',
        label: 'Suspend Condition',
        required: true,
      },
    },
    category: 'Dashboard',
    layout: 'fixed',
    icon: { iconName: 'tire-pressure-warning' },
    description: `**Overview**<br/>
The Suspend Page component is a structural control tool used to prevent UI flickering or partial rendering. It acts as a "Gatekeeper," pausing the rendering of its child components until a specific logic condition is met (e.g., waiting for data to fully load).
Use this component to ensure that users never see empty charts, broken layouts, or "undefined" text while your application is initializing or fetching data.

**1. Logic Configuration**<br/>
The component relies on a single, boolean logic check to determine when to "open the gate."
* **Suspend Condition:** A required expression that returns true or false.
    * **True:** The component Suspends rendering. The children inside it are effectively paused and do not appear in the DOM.
    * **False:** The component Resumes rendering. The children appear and function normally.
* **Example:** Set the condition to \`$.myDataStore.isLoading\`. While the store loads, the page suspends. When loading finishes (false), the page renders.

**2. Content & Composition**<br/>
The Suspend Page is a wrapper. It does not have a visual appearance of its own; it simply controls the visibility lifecycle of the components placed inside it.
* **Allowed Children:** You can wrap major layout blocks to control their initialization.
    * **Layouts:** Flex Panel, Horizontal/Vertical Split Panel.
    * **Containers:** Paper, Tab Panel.
    * **Data:** Data Store Reference.

**3. Strategic Use Cases**<br/>
* **Data Integrity:** Prevent a chart from trying to render before its dataset exists, which often causes errors or visual glitches.
* **Access Control:** Suspend a section of the dashboard until the user's permissions (\`$.user.role\`) have been verified.
* **Smooth UX:** Avoid the "pop-in" effect where elements jump around as they load one by one. Suspend the entire section until everything is ready, then reveal it all at once.`,
  };
};

export default SuspendPageManager;
